/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import PropTypes from 'prop-types';
import { Children, cloneElement, forwardRef, memo, useImperativeHandle, useMemo, useRef } from 'react';
import { borderRadius, borderRadius4px, mg } from "../../styles/general";
import { classNames } from "../../utils";
const AccordionGroup = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  action = {},
  children,
  className,
  collapseOther,
  style
}, reference) => {
  if (!reference) reference = useRef(null);
  const ref = useRef(null);
  const onCollapseOther = (e, onExpand) => {
    if (collapseOther) {
      const accordions = ref.current.querySelectorAll('.DGN-UI-Accordion');
      accordions.forEach(accordion => {
        if (!accordion.contains(e.target)) {
          const summary = accordion.querySelector('.DGN-UI-Accordion-Summary.expanding');
          !!summary && summary.click();
        }
      });
      !!onExpand && onExpand(e);
    }
  };
  useImperativeHandle(reference, () => {
    const currentRef = ref.current || {};
    currentRef.element = ref.current;
    const _instance = {
      ...action
    }; // methods
    _instance.__proto__ = {}; // hidden methods
    currentRef.instance = _instance;
    return currentRef;
  });
  return useMemo(() => {
    return jsx("div", {
      css: AccordionGroupRootCSS,
      ref: ref,
      style: style,
      className: classNames('DGN-UI-Accordion-Group', className)
    }, Children.map(children, child => child && /*#__PURE__*/cloneElement(child, {
      onExpand: e => onCollapseOther(e, child.props.onExpand)
    })));
  }, [children, className, collapseOther, style]);
}));
const AccordionGroupRootCSS = css`
    ${borderRadius4px};
    .DGN-UI-Accordion {
        &:first-of-type {
            &,
            .DGN-UI-Accordion-Summary {
                border-radius: 4px 4px 0px 0px;
            }
            &.expanding {
                ${borderRadius4px};
                ${mg([0, 0, 4, 0])};
                .DGN-UI-Divider {
                    display: none !important;
                }
            }
        }
        &:last-child {
            &,
            .DGN-UI-Accordion-Summary {
                border-radius: 0px 0px 4px 4px;
            }
            &.expanding {
                ${borderRadius4px};
                ${mg([4, 0, 0, 0])};
                .DGN-UI-Accordion-Summary {
                    border-radius: 4px 4px 0px 0px;
                }
                .DGN-UI-Divider {
                    display: none !important;
                }
            }
        }
        &:not(:first-of-type):not(:last-child) {
            &,
            .DGN-UI-Accordion-Summary {
                ${borderRadius(0)};
            }
            &.expanding {
                ${borderRadius4px};
                ${mg([4, 0])};
                .DGN-UI-Accordion-Summary {
                    border-radius: 4px 4px 0px 0px;
                }
                .DGN-UI-Divider {
                    display: none !important;
                }
            }
        }
        &:not(:last-child) {
            .DGN-UI-Divider {
                display: block !important;
            }
            box-shadow: -2px 0px 2px -2px rgba(0, 0, 0, 0.25), 2px 0px 2px -2px rgba(0, 0, 0, 0.25);
            &.expanding,
            &.previousExpanding {
                ${borderRadius4px};
                box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.25);
            }
        }
    }
`;
AccordionGroup.defaultProps = {
  className: '',
  collapseOther: true,
  style: {}
};
AccordionGroup.propTypes = {
  /** The content of the component. */
  children: PropTypes.node,
  /** Class for component. */
  className: PropTypes.string,
  /** If `true`, collapse other when expand */
  collapseOther: PropTypes.bool,
  /** Style inline of component. */
  style: PropTypes.object
};
export default AccordionGroup;